import { mergeRoster } from './merge.js';
import { drainOutbox } from './outbox.js';
import { pullRoster } from '../rpc/roster.js';
import { getMeta, setMeta } from './db.js';

const BASE = Number(import.meta.env?.VITE_POLL_INTERVAL_MS ?? 8000);
const BACKOFFS = [2, 4, 7.5]; // multipliers → 16s, 32s, 60s at base 8s

export function startSync(db, token, opts = {}) {
  const interval = opts.intervalMs ?? BASE;
  const pull = opts.pullFn ?? pullRoster;
  const drain = opts.drainFn ?? ((t) => drainOutbox(db, t));
  const emit = opts.emit ?? (() => {});
  const visibility = opts.visibilityApi ?? {
    get hidden() {
      return document.visibilityState === 'hidden';
    },
    on(cb) {
      document.addEventListener('visibilitychange', cb);
      return () => document.removeEventListener('visibilitychange', cb);
    },
  };

  let stopped = false;
  let backoffIdx = -1;
  let timer = null;

  async function tick() {
    if (stopped) return;
    if (visibility.hidden) {
      schedule(interval);
      return;
    }
    emit('sync:start');
    try {
      const since = await getMeta(db, 'last_synced_at');
      const payload = await pull(token, since);
      // stop() may have landed while the pull was in flight; the token is stale now.
      if (stopped) return;
      await mergeRoster(db, payload);
      await setMeta(db, 'last_synced_at', payload.server_time ?? new Date().toISOString());
      const counts = await drain(token);
      emit('outbox:changed', counts);
      backoffIdx = -1;
      emit('sync:done');
      schedule(interval);
    } catch (e) {
      emit('sync:error', e);
      backoffIdx = Math.min(backoffIdx + 1, BACKOFFS.length - 1);
      schedule(interval * BACKOFFS[backoffIdx]);
    }
  }
  function schedule(ms) {
    clearTimeout(timer);
    if (stopped) return;
    timer = setTimeout(tick, ms);
  }

  const offVis = visibility.on(() => {
    if (!visibility.hidden) {
      clearTimeout(timer);
      tick();
    }
  });
  tick();
  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    offVis();
  };
  const kick = () => {
    clearTimeout(timer);
    tick();
  };
  return { stop, kick };
}
